/**
 * Derivação do estado de SLA no front (v3). A API devolve apenas os campos
 * crus (attendDueAt/solveDueAt/attendedAt/slaPausedAt/…) e o estado é derivado
 * dos timestamps — quando o SLA está pausado os prazos ficam "congelados" no
 * servidor (recomputados na retomada), então Pausado mascara Vencido/A vencer.
 */
import {
  SLA_DUE_SOON_FRACTION,
  slaTargetState,
  type CatalogAreaDetail,
  type CatalogDetail,
  type CatalogItem,
  type SlaState,
  type TicketListItem,
} from '@erp/shared';

// spec 018: a regra do alvo de SLA mora no @erp/shared — a API conta "Prazo em
// risco" com ela e a tela não pode discordar. Reexportada aqui para os
// consumidores de sempre.
export { slaTargetState, type SlaState };
export const DUE_SOON_FRACTION = SLA_DUE_SOON_FRACTION;

export const SLA_STATE_LABELS: Record<SlaState, string> = {
  paused: 'Pausado',
  met: 'Cumprido',
  overdue: 'Vencido',
  due_soon: 'A vencer',
  on_track: 'Em dia',
};

/**
 * Chip compacto de lista/kanban: só os estados que pedem atenção (Vencido /
 * A vencer) do alvo ativo mais crítico. Fechado, cancelado ou pausado ⇒ nada.
 */
export function listSlaState(t: TicketListItem, now: Date): 'overdue' | 'due_soon' | null {
  if (t.closedAt || t.canceledAt || t.slaPausedAt) return null;
  const states: SlaState[] = [];
  const attend = slaTargetState({
    dueAt: t.attendDueAt,
    doneAt: t.attendedAt,
    pausedAt: null,
    startAt: t.createdAt,
    now,
  });
  if (attend) states.push(attend);
  const solve = slaTargetState({
    dueAt: t.solveDueAt,
    doneAt: t.closedAt,
    pausedAt: null,
    startAt: t.createdAt,
    now,
  });
  if (solve) states.push(solve);
  if (states.includes('overdue')) return 'overdue';
  if (states.includes('due_soon')) return 'due_soon';
  return null;
}

/** Meta em minutos legível: "45min", "2h", "2h 30min". */
export function formatSlaGoal(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

/** "faltam 2h 30min" / "vencido há 45min" (horas corridas, aproximação de UI). */
export function formatDueDistance(dueAt: string | Date, now: Date): string {
  const diffMs = new Date(dueAt).getTime() - now.getTime();
  const abs = Math.max(1, Math.round(Math.abs(diffMs) / 60_000));
  const label = abs >= 1440 ? `${Math.floor(abs / 1440)}d ${Math.floor((abs % 1440) / 60)}h` : formatSlaGoal(abs);
  return diffMs >= 0 ? `faltam ${label}` : `vencido há ${label}`;
}

// ── Catálogo: navegação na árvore catálogo → área → item ────────────────────

export interface CatalogPath {
  catalog: CatalogDetail;
  area: CatalogAreaDetail;
  item: CatalogItem;
}

/** Localiza um item pelo id dentro das árvores de catálogo da mesa. */
export function findCatalogPath(trees: CatalogDetail[], itemId: number): CatalogPath | null {
  for (const catalog of trees) {
    for (const area of catalog.areas) {
      const item = area.items.find((i) => i.id === itemId);
      if (item) return { catalog, area, item };
    }
  }
  return null;
}
